<script setup>
import { ref, watch, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  NCard,
  NInputNumber,
  NForm,
  NFormItem,
  NCheckbox,
  NInput,
  NButton,
  NSelect,
  NSpace,
  NTag,
  NDynamicInput,
  NPageHeader,
  NResult,
  NSwitch,
} from "naive-ui";

// API utilities
import { 
  API_BASE_URL, 
  saveProvider as saveProviderService,
  formatProviderDataForForm,
} from "@/services/provider";
import { authToken, isAuthenticated, authFetch } from "@/services/auth";

const route = useRoute();
const router = useRouter();

// Determine if we're editing (id in URL) or creating new
const isEditMode = computed(() => !!route.params.id);
const pageTitle = computed(() => isEditMode.value ? "Quick Edit Provider" : "Add New Provider");

// Form data
const formData = ref({
  provider_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: 0,
  specializations: [],
  phone_numbers: [],
  tags: [],
  additional_info: "",
  email: "",
  website: "",
});

// State management
const loading = ref(false);
const saving = ref(false);
const success = ref(false);
const error = ref(null);
const showPreview = ref(false);
const markAsValidated = ref(false);

// Fetch provider data if in edit mode
onMounted(async () => {
  if (isEditMode.value) {
    try {
      loading.value = true;
      
      // Check if authenticated
      if (!isAuthenticated.value) {
        throw new Error("Authentication required");
      }
      
      // Fetch full provider data
      const response = await authFetch(`${API_BASE_URL}/providers/${route.params.id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const providerData = await response.json();
      
      // Populate form with provider data
      formData.value = {
        provider_name: providerData.provider_name || "",
        address_line1: providerData.address_line1 || "",
        address_line2: providerData.address_line2 || "",
        city: providerData.city || "",
        state: providerData.state || "",
        postal_code: providerData.postal_code || 0,
        specializations: providerData.specializations || [],
        tags: providerData.tags || [],
        phone_numbers: providerData.phone_numbers || [],
        additional_info: providerData.additional_info || "",
        email: providerData.email || "",
        website: providerData.website || providerData.website_url || "",
      };
      
    } catch (err) {
      error.value = "Failed to load provider data";
      console.error(err);
    } finally {
      loading.value = false;
    }
  }
});

// Reset form
const resetForm = () => {
  formData.value = {
    provider_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: 0,
    specializations: [],
    phone_numbers: [],
    tags: [],
    additional_info: "",
    email: "",
    website: "",
  };
};

// Options
const specializationOptions = [
  { label: "Cardiology", value: "Cardiology" },
  { label: "Dermatology", value: "Dermatology" },
  { label: "Endocrinology", value: "Endocrinology" },
  { label: "Gastroenterology", value: "Gastroenterology" },
  { label: "General Practice", value: "General Practice" },
  { label: "Neurology", value: "Neurology" },
  { label: "Oncology", value: "Oncology" },
  { label: "Orthopedics", value: "Orthopedics" },
  { label: "Pediatrics", value: "Pediatrics" },
  { label: "Psychiatry", value: "Psychiatry" },
  { label: "Allergy & Immunology", value: "Allergy & Immunology" },
  { label: "Anesthesiology", value: "Anesthesiology" },
  { label: "Emergency Medicine", value: "Emergency Medicine" },
  { label: "Family Medicine", value: "Family Medicine" },
  { label: "General Surgery", value: "General Surgery" },
  { label: "Geriatrics", value: "Geriatrics" },
  { label: "Gynecology", value: "Gynecology" },
  { label: "Hematology", value: "Hematology" },
  { label: "Infectious Disease", value: "Infectious Disease" },
  { label: "Internal Medicine", value: "Internal Medicine" },
  { label: "Nephrology", value: "Nephrology" },
  { label: "Neurosurgery", value: "Neurosurgery" },
  { label: "Obstetrics & Gynecology", value: "Obstetrics & Gynecology" },
  { label: "Ophthalmology", value: "Ophthalmology" },
  { label: "Otolaryngology (ENT)", value: "Otolaryngology" },
  { label: "Pain Management", value: "Pain Management" },
  { label: "Pathology", value: "Pathology" },
  { label: "Physical Medicine & Rehabilitation", value: "Physical Medicine & Rehabilitation" },
  { label: "Plastic Surgery", value: "Plastic Surgery" },
  { label: "Pulmonology", value: "Pulmonology" },
  { label: "Radiology", value: "Radiology" },
  { label: "Rheumatology", value: "Rheumatology" },
  { label: "Sports Medicine", value: "Sports Medicine" },
  { label: "Thoracic Surgery", value: "Thoracic Surgery" },
  { label: "Urology", value: "Urology" },
  { label: "Vascular Surgery", value: "Vascular Surgery" },
  { label: "Ayurveda", value: "Ayurveda" },
  { label: "Siddha Medicine", value: "Siddha Medicine" },
  { label: "Unani Medicine", value: "Unani Medicine" },
  { label: "Homeopathy", value: "Homeopathy" },
  { label: "Yoga Therapy", value: "Yoga Therapy" },
  { label: "Naturopathy", value: "Naturopathy" },
  { label: "Panchakarma", value: "Panchakarma" },
  { label: "Marma Therapy", value: "Marma Therapy" },
  { label: "Acupressure", value: "Acupressure" },
  { label: "Traditional Chinese Medicine", value: "Traditional Chinese Medicine" },
  { label: "Acupuncture", value: "Acupuncture" },
  { label: "Chiropractic", value: "Chiropractic" },
  { label: "Osteopathy", value: "Osteopathy" },
  { label: "Herbal Medicine", value: "Herbal Medicine" },
  { label: "Aromatherapy", value: "Aromatherapy" },
  { label: "Reflexology", value: "Reflexology" },
  { label: "Reiki", value: "Reiki" },
  { label: "Massage Therapy", value: "Massage Therapy" },
  { label: "Nutritional Therapy", value: "Nutritional Therapy" },
  { label: "Hypnotherapy", value: "Hypnotherapy" },
  { label: "Clinical Psychology", value: "Clinical Psychology" },
  { label: "Counseling", value: "Counseling" },
  { label: "Art Therapy", value: "Art Therapy" },
  { label: "Music Therapy", value: "Music Therapy" },
  { label: "Physiotherapy", value: "Physiotherapy" },
  { label: "Occupational Therapy", value: "Occupational Therapy" },
  { label: "Speech Therapy", value: "Speech Therapy" },
  { label: "Dietetics", value: "Dietetics" },
  { label: "Optometry", value: "Optometry" },
  { label: "Audiology", value: "Audiology" },
  { label: "Podiatry", value: "Podiatry" },
  { label: "Pharmacy", value: "Pharmacy" }
];

const stateOptions = [
  { label: "Andhra Pradesh", value: "Andhra Pradesh" },
  { label: "Arunachal Pradesh", value: "Arunachal Pradesh" },
  { label: "Assam", value: "Assam" },
  { label: "Bihar", value: "Bihar" },
  { label: "Chhattisgarh", value: "Chhattisgarh" },
  { label: "Goa", value: "Goa" },
  { label: "Gujarat", value: "Gujarat" },
  { label: "Haryana", value: "Haryana" },
  { label: "Himachal Pradesh", value: "Himachal Pradesh" },
  { label: "Jharkhand", value: "Jharkhand" },
  { label: "Karnataka", value: "Karnataka" },
  { label: "Kerala", value: "Kerala" },
  { label: "Madhya Pradesh", value: "Madhya Pradesh" },
  { label: "Maharashtra", value: "Maharashtra" },
  { label: "Manipur", value: "Manipur" },
  { label: "Meghalaya", value: "Meghalaya" },
  { label: "Mizoram", value: "Mizoram" },
  { label: "Nagaland", value: "Nagaland" },
  { label: "Odisha", value: "Odisha" },
  { label: "Punjab", value: "Punjab" },
  { label: "Rajasthan", value: "Rajasthan" },
  { label: "Sikkim", value: "Sikkim" },
  { label: "Tamil Nadu", value: "Tamil Nadu" },
  { label: "Telangana", value: "Telangana" },
  { label: "Tripura", value: "Tripura" },
  { label: "Uttar Pradesh", value: "Uttar Pradesh" },
  { label: "Uttarakhand", value: "Uttarakhand" },
  { label: "West Bengal", value: "West Bengal" },
  { label: "Andaman and Nicobar Islands", value: "Andaman and Nicobar Islands" },
  { label: "Chandigarh", value: "Chandigarh" },
  { label: "Dadra and Nagar Haveli and Daman and Diu", value: "Dadra and Nagar Haveli and Daman and Diu" },
  { label: "Delhi", value: "Delhi" },
  { label: "Jammu and Kashmir", value: "Jammu and Kashmir" },
  { label: "Ladakh", value: "Ladakh" },
  { label: "Lakshadweep", value: "Lakshadweep" },
  { label: "Puducherry", value: "Puducherry" },
];

// Save provider
const saveProvider = async () => {
  saving.value = true;
  error.value = null;

  try {
    // Check if authenticated
    if (!isAuthenticated.value) {
      throw new Error("Authentication required");
    }

    // Create payload with all form data
    const payload = {
      ...formData.value,
      postal_code: parseInt(formData.value.postal_code) || null,
      status: 'unverified' // Always set status to unverified for quick edits
    };

    // Set ID if in edit mode
    if (isEditMode.value) {
      payload.id = route.params.id;
    }

    // Use the save service
    const savedProvider = await saveProviderService(authToken.value, payload);
    
    success.value = true;
    
    // Reset form if creating new
    if (!isEditMode.value) {
      resetForm();
    }
    
    // Redirect after success
    setTimeout(() => {
      router.push('/validator-search');
    }, 2000);
    
  } catch (err) {
    console.error('Save error:', err);
    error.value = err.message || "Failed to save provider";
  } finally {
    saving.value = false;
  }
};

// Show preview
const showPreviewForm = () => {
  showPreview.value = true;
  window.scrollTo(0, 0);
};

// Hide preview
const hidePreview = () => {
  showPreview.value = false;
};

// Cancel and go back
const handleCancel = () => {
  router.back();
};

// Helper functions for dynamic inputs
const createPhoneNumber = () => "";
const createTag = () => "";
</script>

<template>
  <div class="provider-edit-container">
    <NPageHeader 
      :title="pageTitle" 
      @back="handleCancel"
    />
    
    <!-- Loading state -->
    <div v-if="loading" class="loading-container">
      <NCard>
        <div class="loading-content">
          <p>Loading provider data...</p>
        </div>
      </NCard>
    </div>
    
    <!-- Success message -->
    <NResult 
      v-else-if="success" 
      status="success" 
      :title="isEditMode ? 'Provider Updated!' : 'Provider Created!'" 
      description="Your provider information has been saved successfully and marked as unverified for review."
    >
      <template #footer>
        <NButton type="primary" @click="router.push('/my-providers')">
          Return to My Providers
        </NButton>
      </template>
    </NResult>
    
    <!-- Error message -->
    <NResult 
      v-else-if="error" 
      status="error" 
      title="Something Went Wrong" 
      :description="error"
    >
      <template #footer>
        <NButton @click="error = null">
          Try Again
        </NButton>
      </template>
    </NResult>
    
    <!-- Preview Mode -->
    <div v-else-if="showPreview" class="preview-mode">
      <NCard title="Review Your Changes" class="preview-card">
        <div class="preview-section">
          <div class="preview-header">
            <h2>{{ formData.provider_name || "Provider Name" }}</h2>
          </div>

          <div class="preview-content">
            <div class="preview-left">
              <div
                v-if="formData.specializations.length > 0"
                class="preview-item"
              >
                <h4>🏥 Specializations</h4>
                <div class="tags-container">
                  <NTag
                    v-for="spec in formData.specializations"
                    :key="spec"
                    type="primary"
                    class="preview-tag"
                  >
                    {{ spec }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.tags.length > 0" class="preview-item">
                <h4>🏷️ Tags</h4>
                <div class="tags-container">
                  <NTag
                    v-for="tag in formData.tags"
                    :key="tag"
                    type="info"
                    class="preview-tag"
                  >
                    {{ tag }}
                  </NTag>
                </div>
              </div>

              <div v-if="formData.additional_info" class="preview-item">
                <h4>ℹ️ About</h4>
                <p class="preview-text">{{ formData.additional_info }}</p>
              </div>
            </div>

            <div class="preview-right">
              <div class="preview-item">
                <h4>📍 Address</h4>
                <div
                  class="preview-address"
                  v-if="formData.address_line1 || formData.city"
                >
                  <div v-if="formData.address_line1">
                    {{ formData.address_line1 }}
                  </div>
                  <div v-if="formData.address_line2">
                    {{ formData.address_line2 }}
                  </div>
                  <div
                    v-if="
                      formData.city || formData.state || formData.postal_code
                    "
                  >
                    {{ formData.city
                    }}{{
                      formData.city && (formData.state || formData.postal_code)
                        ? ", "
                        : ""
                    }}{{ formData.state }} {{ formData.postal_code }}
                  </div>
                </div>
                <div v-else class="preview-empty">No address provided</div>
              </div>

              <div
                v-if="formData.phone_numbers.length > 0"
                class="preview-item"
              >
                <h4>📞 Phone Numbers</h4>
                <div
                  v-for="phone in formData.phone_numbers"
                  :key="phone"
                  class="contact-item"
                >
                  {{ phone }}
                </div>
              </div>

              <div v-if="formData.email" class="preview-item">
                <h4>📧 Email</h4>
                <div class="contact-item">{{ formData.email }}</div>
              </div>

              <div v-if="formData.website" class="preview-item">
                <h4>🌐 Website</h4>
                <div class="contact-item">{{ formData.website }}</div>
              </div>
            </div>
          </div>
          
          <div class="status-notice">
            <h4>⚠️ Important Notice</h4>
            <p>This provider will be marked as <strong>"unverified"</strong> and will require review before being publicly visible.</p>
          </div>
        </div>
        
        <template #action>
          <NSpace justify="space-between">
            <NButton @click="hidePreview">← Back to Edit</NButton>
            <NButton 
              type="primary" 
              @click="saveProvider" 
              :loading="saving"
            >
              {{ isEditMode ? "✏️ Update Provider" : "➕ Create Provider" }}
            </NButton>
          </NSpace>
        </template>
      </NCard>
    </div>
    
    <!-- Edit Form -->
    <div v-else class="edit-form">
      <NCard title="Provider Information" class="form-card">
        <NForm :model="formData" label-placement="top">
          <!-- Basic Information -->
          <div class="form-section">
            <h3 class="section-title">📋 Basic Information</h3>
            
            <NFormItem label="Provider Name" required>
              <NInput
                v-model:value="formData.provider_name"
                placeholder="Enter provider name"
                size="large"
              />
            </NFormItem>

            <NFormItem label="Specializations">
              <NSelect
                v-model:value="formData.specializations"
                multiple
                :options="specializationOptions"
                placeholder="Select specializations"
                filterable
                tag
                size="large"
              />
            </NFormItem>

            <NFormItem label="Tags">
              <NDynamicInput
                v-model:value="formData.tags"
                :on-create="createTag"
                placeholder="Add tags"
              />
            </NFormItem>

            <NFormItem label="Additional Information">
              <NInput
                v-model:value="formData.additional_info"
                type="textarea"
                placeholder="Enter additional information about the provider"
                :rows="3"
                size="large"
              />
            </NFormItem>
          </div>
          
          <!-- Address Information -->
          <div class="form-section">
            <h3 class="section-title">📍 Address Information</h3>
            
            <NFormItem label="Address Line 1">
              <NInput
                v-model:value="formData.address_line1"
                placeholder="Street address, building number"
                size="large"
              />
            </NFormItem>

            <NFormItem label="Address Line 2">
              <NInput
                v-model:value="formData.address_line2"
                placeholder="Apartment, suite, unit, floor (optional)"
                size="large"
              />
            </NFormItem>

            <div class="form-row">
              <NFormItem label="City" class="form-col">
                <NInput
                  v-model:value="formData.city"
                  placeholder="Enter city"
                  size="large"
                />
              </NFormItem>

              <NFormItem label="State" class="form-col">
                <NSelect
                  v-model:value="formData.state"
                  :options="stateOptions"
                  placeholder="Select state"
                  filterable
                  size="large"
                />
              </NFormItem>

              <NFormItem label="Postal Code" class="form-col">
                <NInputNumber
                  v-model:value="formData.postal_code"
                  placeholder="110001"
                  size="large"
                  :show-button="false"
                  :min="100000"
                  :max="999999"
                  class="w-full"
                />
              </NFormItem>
            </div>

            <!-- Address Preview -->
            <div
              class="address-preview"
              v-if="formData.address_line1 || formData.city"
            >
              <h4>📮 Address Preview:</h4>
              <div class="preview-address">
                <div v-if="formData.address_line1">
                  {{ formData.address_line1 }}
                </div>
                <div v-if="formData.address_line2">
                  {{ formData.address_line2 }}
                </div>
                <div
                  v-if="formData.city || formData.state || formData.postal_code"
                >
                  {{ formData.city
                  }}{{
                    formData.city && (formData.state || formData.postal_code)
                      ? ", "
                      : ""
                  }}{{ formData.state }} {{ formData.postal_code }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- Contact Information -->
          <div class="form-section">
            <h3 class="section-title">📞 Contact Information</h3>
            
            <NFormItem label="Phone Numbers">
              <NDynamicInput
                v-model:value="formData.phone_numbers"
                :on-create="createPhoneNumber"
                placeholder="Add phone number"
                #="{ value, index }"
              >
                <NInput
                  v-model:value="formData.phone_numbers[index]"
                  placeholder="(555) 123-4567"
                  size="large"
                />
              </NDynamicInput>
            </NFormItem>

            <NFormItem label="Email Address">
              <NInput
                v-model:value="formData.email"
                placeholder="provider@example.com"
                type="email"
                size="large"
              />
            </NFormItem>

            <NFormItem label="Website">
              <NInput
                v-model:value="formData.website"
                placeholder="https://www.example.com"
                size="large"
              />
            </NFormItem>
          </div>
        </NForm>
        
        <template #action>
          <NSpace justify="space-between">
            <NButton @click="handleCancel" size="large">Cancel</NButton>
            <NSpace vertical :size="12">
              <!-- Validation checkbox -->
              <div class="flex items-center justify-end space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <NCheckbox 
                  v-model:checked="markAsValidated"
                  class="text-green-800"
                >
                  <span class="text-sm font-medium text-green-800">
                    Mark as validated
                  </span>
                </NCheckbox>
              </div>
              
              <!-- Button row -->
              <NSpace justify="end">
                <NButton @click="showPreviewForm" size="large" type="info">
                  👁️ Preview Changes
                </NButton>
                <NButton
                  type="primary"
                  @click="saveProvider"
                  :loading="saving"
                  size="large"
                >
                  {{ isEditMode ? "✏️ Update Provider" : "➕ Create Provider" }}
                </NButton>
              </NSpace>
            </NSpace>
          </NSpace>
        </template>
      </NCard>
    </div>
  </div>
</template>

<style scoped>
.provider-edit-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.loading-container {
  margin-top: 2rem;
}

.loading-content {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.form-card,
.preview-card {
  margin-bottom: 2rem;
}

.form-section {
  margin-bottom: 2.5rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #f0f0f0;
}

.form-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.section-title {
  margin: 0 0 1.5rem 0;
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #18a058;
}

.form-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
}

.form-col {
  margin-bottom: 0;
}

.address-preview {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #18a058;
}

.address-preview h4 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 0.9rem;
}

.preview-address {
  color: #555;
  line-height: 1.5;
}

.preview-section {
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e0e0e6;
}

.preview-header {
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e0e6;
}

.preview-header h2 {
  margin: 0;
  color: #333;
  font-size: 1.8rem;
}

.preview-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.preview-item {
  margin-bottom: 1.5rem;
}

.preview-item:last-child {
  margin-bottom: 0;
}

.preview-item h4 {
  margin: 0 0 0.75rem 0;
  color: #333;
  font-size: 1rem;
  font-weight: 600;
}

.preview-text {
  margin: 0;
  color: #555;
  line-height: 1.6;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.preview-tag {
  margin: 0;
}

.contact-item {
  color: #555;
  margin-bottom: 0.5rem;
  padding: 0.25rem 0;
}

.contact-item:last-child {
  margin-bottom: 0;
}

.preview-empty {
  color: #999;
  font-style: italic;
}

.status-notice {
  padding: 1rem;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  margin-top: 1rem;
}

.status-notice h4 {
  margin: 0 0 0.5rem 0;
  color: #856404;
  font-size: 1rem;
}

.status-notice p {
  margin: 0;
  color: #856404;
}

@media (max-width: 768px) {
  .provider-edit-container {
    padding: 1rem 0.5rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .preview-content {
    grid-template-columns: 1fr;
  }
  
  .section-title {
    font-size: 1rem;
  }
}
</style>